'use client'

import Image from 'next/image'
import { useEffect, useState, useSyncExternalStore } from 'react'

export const PRODUCTION_WELCOME_STORAGE_KEY = 'partyamt_production_welcome_seen_v1'
const PRODUCTION_WELCOME_STORAGE_EVENT = 'partyamt-production-welcome-storage'

type ProductionWelcomePopupProps = {
  enabled?: boolean
}

function subscribeToWelcomeStorage(callback: () => void) {
  window.addEventListener(PRODUCTION_WELCOME_STORAGE_EVENT, callback)
  return () => window.removeEventListener(PRODUCTION_WELCOME_STORAGE_EVENT, callback)
}

function getWelcomeStorageSnapshot() {
  try {
    return window.localStorage.getItem(PRODUCTION_WELCOME_STORAGE_KEY) === 'seen'
  } catch {
    return false
  }
}

function getServerWelcomeStorageSnapshot() {
  return true
}

export function ProductionWelcomePopup({ enabled = process.env.NODE_ENV === 'production' }: ProductionWelcomePopupProps) {
  const [dismissedForPageView, setDismissedForPageView] = useState(false)
  const dismissed = useSyncExternalStore(
    subscribeToWelcomeStorage,
    getWelcomeStorageSnapshot,
    getServerWelcomeStorageSnapshot,
  )

  useEffect(() => {
    if (!enabled) return
    window.dispatchEvent(new Event(PRODUCTION_WELCOME_STORAGE_EVENT))
  }, [enabled])

  if (!enabled || dismissed || dismissedForPageView) return null

  function handleDismiss() {
    try {
      window.localStorage.setItem(PRODUCTION_WELCOME_STORAGE_KEY, 'seen')
    } catch {
      // Storage can fail in private modes; the popup should still close for this page view.
    }
    setDismissedForPageView(true)
    window.dispatchEvent(new Event(PRODUCTION_WELCOME_STORAGE_EVENT))
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/45 px-4 py-6" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="production-welcome-title"
        className="relative w-full max-w-md overflow-hidden border border-ink bg-paper p-6 text-ink shadow-[8px_8px_0_#111111]"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          aria-hidden="true"
          data-testid="production-welcome-confetti"
        >
          <span className="absolute left-5 top-8 h-2 w-7 rotate-12 bg-signal" />
          <span className="absolute left-16 top-5 h-2 w-6 -rotate-12 bg-ink/40" />
          <span className="absolute left-1/3 top-8 h-3 w-3 rotate-45 border border-signal" />
          <span className="absolute left-1/2 top-4 h-4 w-4 border border-ink/70" />
          <span className="absolute right-28 top-12 h-2 w-8 -rotate-12 bg-ink/35" />
          <span className="absolute right-10 top-8 h-3 w-3 rotate-12 bg-signal/40" />
          <span className="absolute left-10 top-1/3 h-5 w-2 -rotate-12 bg-ink/50" />
          <span className="absolute right-20 top-1/3 h-6 w-2 rotate-12 bg-signal" />
          <span className="absolute right-8 top-1/2 h-3 w-3 border border-ink/60" />
          <span className="absolute left-12 bottom-28 h-3 w-3 rotate-45 bg-signal/45" />
          <span className="absolute left-1/3 bottom-20 h-2 w-7 -rotate-6 bg-ink/40" />
          <span className="absolute right-32 bottom-24 h-3 w-3 border border-signal" />
          <span className="absolute right-10 bottom-28 h-2 w-8 rotate-6 bg-signal/40" />
          <span className="absolute bottom-12 left-20 h-2 w-5 rotate-12 bg-ink/35" />
        </div>

        <div className="relative">
          <p className="mb-3 font-mono text-xs font-bold uppercase text-signal">Neues Design</p>
          <h2 id="production-welcome-title" className="flex flex-wrap items-end gap-x-3 gap-y-2 text-2xl font-bold leading-tight">
            <span>Willkommen im neuen</span>
            <Image
              src="/images/partyamt_logo.png"
              alt="Partyamt"
              width={600}
              height={79}
              className="h-7 w-auto max-w-[220px] sm:h-8"
              priority
            />
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-6">
            <p>Wir haben partyamt.de komplett überarbeitet und starten mit einem neuen Design.</p>
            <p>Die wichtigsten Funktionen bleiben im Kern wie gewohnt.</p>
            <p>
              Wenn dir etwas auffällt, du einen Wunsch hast oder eine Verbesserung vorschlagen möchtest, nutz gerne den
              Feedback-Button unten rechts.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="mt-6 w-full bg-ink px-4 py-3 font-mono text-xs font-bold uppercase text-paper transition-colors hover:bg-signal"
          >
            Alles klar
          </button>
        </div>
      </div>
    </div>
  )
}
